import React from "react";
import { FormattedMessage } from "react-intl";
import { css } from "@emotion/react";
import moment from "moment";
import { PrimaryLink } from "components/Links";
import { RESOURCE_TYPES, COMMENTABLE_TYPES, POLYMORPHIC_RESOURCE_TYPES, APPLICANT_TYPES } from "constants/index";
import { UserFullName } from "components/User";

const TIMELINE_EVENTS = {
  assignmentCreated: "platform:backend:assignment:created",
  assignmentUpdated: "platform:backend:assignment:updated",
  assignmentDestroyed: "platform:backend:assignment:destroyed",
  profileCreated: "platform:backend:profile:created",
  profileUpdated: "platform:backend:profile:updated",
  profileEducationCreated: "platform:backend:profile_education:created",
  profileEducationUpdated: "platform:backend:profile_education:updated",
  profileEducationDestroyed: "platform:backend:profile_education:destroyed",
  profileLinkCreated: "platform:backend:profile_link:created",
  profileLinkUpdated: "platform:backend:profile_link:updated",
  profileLinkDestroyed: "platform:backend:profile_link:destroyed",
  profileScheduleCreated: "platform:backend:profile_schedule:created",
  profileScheduleUpdated: "platform:backend:profile_schedule:updated",
  profileScheduleDestroyed: "platform:backend:profile_schedule:destroyed",
  profileVacationCreated: "platform:backend:profile_vacation:created",
  profileVacationUpdated: "platform:backend:profile_vacation:updated",
  profileVacationDestroyed: "platform:backend:profile_vacation:destroyed",
  profileWorkExperienceCreated: "platform:backend:profile_work_experience:created",
  profileWorkExperienceUpdated: "platform:backend:profile_work_experience:updated",
  profileWorkExperienceDestroyed: "platform:backend:profile_work_experience:destroyed",
  profileListCreated: "platform:backend:profile_list:created",
  profileListUpdated: "platform:backend:profile_list:updated",
  profileListDestroyed: "platform:backend:profile_list:destroyed",
  interviewResponseCreated: "platform:backend:interview_response:created",
  interviewResponseUpdated: "platform:backend:interview_response:updated",
  interviewResponseDestroyed: "platform:backend:interview_response:destroyed",
  coderbyteChallengeResponseCreated: "platform:backend:coderbyte_challenge_response:created",
  coderbyteChallengeResponseUpdated: "platform:backend:coderbyte_challenge_response:updated",
  coderbyteChallengeResponseDestroyed: "platform:backend:coderbyte_challenge_response:destroyed",
  timeCardCreated: "platform:backend:time_card:created",
  timeCardUpdated: "platform:backend:time_card:updated",
  timeCardDestroyed: "platform:backend:time_card:destroyed",
  timeSlipCreated: "platform:backend:time_slip:created",
  timeSlipUpdated: "platform:backend:time_slip:updated",
  timeSlipDestroyed: "platform:backend:time_slip:destroyed",
  missionRoleApplicantCreated: "platform:backend:mission_role_applicant:created",
  missionRoleApplicantUpdated: "platform:backend:mission_role_applicant:updated",
  missionRoleApplicantDestroyed: "platform:backend:mission_role_applicant:destroyed",
  missionRoleInvitationCreated: "platform:backend:mission_role_invitation:created",
  missionInterviewCreated: "platform:backend:mission_interview:created",
  missionInterviewUpdated: "platform:backend:mission_interview:updated",
  missionInterviewDestroyed: "platform:backend:mission_interview:destroyed",
  commentCreated: "platform:backend:comment:created",
  commentUpdated: "platform:backend:comment:updated",
  commentDestroyed: "platform:backend:comment:destroyed",
  deelTaskCreated: "platform:backend:deel_invoices_task:created",
  deelTaskUpdated: "platform:backend:deel_invoices_task:updated",
};

const TIMELINE_EVENT_TYPES = {
  questionAdded: "Question Added",
  questionUpdated: "Question Updated",
  questionRemoved: "Question Removed",
  missionRoleCreated: "Mission Role Created",
  missionRoleUpdated: "Mission Role Updated",
  missionRoleDestroyed: "Mission Role Destroyed",
  codeChallengeAdded: "Code Challenge Added",
  codeChallengeRemoved: "Code Challenge Removed",
  disciplineAdded: "Discipline Added",
  disciplineRemoved: "Discipline Removed",
  locationRestrictionAdded: "Location Restriction Added",
  locationRestrictionRemoved: "Location Restriction Removed",
  requiredSkillAdded: "Required Skill Added",
  requiredSkillRemoved: "Required Skill Removed",
  niceToHaveSkillAdded: "Nice-to-Have Skill Added",
  niceToHaveSkillRemoved: "Nice-to-Have Skill Removed",
  applicationReceived: "Application Received",
  applicationAtsMove: "Application ATS Move",
  invitationSent: "Invitation Sent",
  invitationAccepted: "Invitation Accepted",
};

/**
 * Formats the data in resourceContext into React elements
 *
 * @param {String} resourceType
 * @param {Object} resourceContext
 * @param {Object} userProp
 * @param {Object} impersonator
 * @param {String} createdAt
 *
 * @returns Object
 */
function formatTimelineResourceData({
  resourceType,
  resourceContext,
  resourceId,
  profileId,
  userProp,
  impersonator,
  createdAt,
  payload,
  links,
  roleLinkComponent: RoleLink,
  missionLinkComponent: MissionLink,
  organizationLinkComponent: OrganizationLink,
}) {
  const {
    user,
    role,
    url,
    commentableType,
    description,
    acceptedBy,
    status,
    degreeName,
    schoolName,
    linkType,
    weekday,
    vacationEndDate,
    vacationStartDate,
    companyName,
    experienceRole,
    listName,
    profile,
    timeCardMonthPeriod,
    timeCardStatus,
    activityDate,
    activityType,
    teamName,
    timeLogged,
    challengeName,
    interviewName,
    interviewer,
    mission,
    codeChallenge,
    location,
    tag,
    question,
    fromStage,
    toStage,
  } = getDataByResourceType(resourceType, resourceContext, payload, userProp);
  const adminUser = {
    name: <FormattedMessage id="Global.MissionUsername" />,
  };

  return {
    user: impersonator?.id ? (
      <>
        <UserFullName data={user} />
        <span> (impersonated by {<UserFullName data={impersonator} />})</span>
      </>
    ) : (
      <UserFullName data={user} />
    ),
    role: <RoleLink mission={role?.mission} missionRole={role} withOverlay />,
    date: <b>{moment(createdAt).fromNow()}</b>,
    name: <UserFullName data={userProp?.profile || userProp || adminUser} />,
    url: <PrimaryLink to={url}>Calendly</PrimaryLink>,
    note: <PrimaryLink to={links.note(profileId, resourceId)}>note</PrimaryLink>,
    comment: commentableType == COMMENTABLE_TYPES.comment && (
      <FormattedMessage
        id={`Timeline.TimelineEvent.CommentCreated.partial`}
        values={{
          comment: <PrimaryLink to={links.note(profileId, resourceContext.commentable?.id)}>comment</PrimaryLink>,
        }}
      />
    ),
    acceptedBy: acceptedBy?.id && (
      <FormattedMessage
        id="Timeline.TimelineEvent.DeelTaskAcceptedBy"
        values={{ name: <UserFullName data={acceptedBy} /> }}
      />
    ),
    description: description && (
      <FormattedMessage id="Timeline.TimelineEvent.DeelTaskDescription" values={{ description }} />
    ),
    profileFullName: <UserFullName data={profile} />,
    interviewerFullName: <UserFullName data={interviewer} />,
    mission: <MissionLink data={mission} withOverlay />,
    organization: (
      <OrganizationLink data={{ name: mission?.organizationName, slug: mission?.organizationSlug }} withOverlay />
    ),
    location: <b>{location?.name}</b>,
    codeChallenge: <b>{codeChallenge?.name}</b>,
    tag: <b>{tag?.name}</b>,
    question: <span css={styles.question_wording}>{question?.wording}</span>,
    fromStage: (
      <b>
        <FormattedMessage id={`Missions.MissionCandidateList.State.${APPLICANT_TYPES[fromStage]}`} />
      </b>
    ),
    toStage: (
      <b>
        <FormattedMessage id={`Missions.MissionCandidateList.State.${APPLICANT_TYPES[toStage]}`} />
      </b>
    ),
    degreeName,
    schoolName,
    linkType,
    status,
    weekday,
    vacationEndDate,
    vacationStartDate,
    companyName,
    experienceRole,
    listName,
    timeCardMonthPeriod,
    timeCardStatus,
    activityDate,
    activityType,
    teamName,
    timeLogged,
    challengeName,
    interviewName,
  };
}

/**
 * Get polymorphic resource data structure
 * @param {String} baseField
 * @param {String} polyField
 * @param {Object} userProp
 * @param {Object} resourceContext
 *
 * @returns Object
 */
function getPolymorphicResourceData(baseField, polyField, userProp, resourceContext) {
  const polymorphicResourceType = POLYMORPHIC_RESOURCE_TYPES[RESOURCE_TYPES[polyField]];
  const polymorphicType = polymorphicResourceType?.[resourceContext?.[polyField]?.type];
  const relatedEntities = polymorphicResourceType?.relatedEntities;
  let data = { user: userProp, [baseField]: resourceContext?.[baseField] };

  if (polymorphicType) data[polymorphicType] = resourceContext?.[polyField];

  (relatedEntities || []).forEach((key) => (data[key] = resourceContext?.[polyField]?.[key]));

  return data;
}

/**
 * Get the formatted message component for titles via event type
 *
 * @param {*} eventType
 * @param {*} payload
 * @param {*} userProp
 *
 * @return String
 */
const getTimelineTitleKeyByEventType = (eventType, payload, userProp) => {
  switch (eventType) {
    case TIMELINE_EVENT_TYPES.applicationAtsMove: {
      if (payload?.state) {
        const state = payload?.state.slice(-1)?.shift();
        const userAffectedOwnProfile = userProp?.profile?.id == payload?.resourceContext?.profile?.id;

        if (userAffectedOwnProfile) {
          return `MissionRoleApplicant.MemberMovedApplication:${state}`;
        }
      }

      return eventType;
    }

    default:
      return eventType;
  }
};

/**
 * Get the formatted message keyname for titles
 *
 * @param {*} kName
 * @param {*} payload
 * @param {*} location
 *
 * @returns String
 */
const getTimelineTitleKey = (kName, payload, userProp) => {
  switch (kName) {
    // Profile updates
    case TIMELINE_EVENTS.profileUpdated:
      // Profile archived
      if (payload?.archivedSince?.slice(-1)) {
        return "UserArchived";
        // Profile approved
      } else if (payload?.approvedById?.slice(-1)) {
        return "UserApproved";
      }
      return kName;

    case TIMELINE_EVENTS.missionRoleApplicantCreated:
      return `MissionRole:${payload?.state}`;

    case TIMELINE_EVENTS.missionRoleApplicantUpdated:
      if (payload?.state) {
        return `MissionRole:${payload?.state.slice(-1)}`;
      }

      return kName;

    case TIMELINE_EVENTS.missionRoleApplicantDestroyed:
      return `MissionRoleApplicant.Destroyed`;

    case TIMELINE_EVENTS.missionRoleInvitationCreated:
      return `MissionRoleInvited`;

    case TIMELINE_EVENTS.missionInterviewCreated:
      return `MissionInterviewCreated`;

    case TIMELINE_EVENTS.missionInterviewUpdated:
      return `MissionInterviewUpdated`;

    case TIMELINE_EVENTS.missionInterviewDestroyed:
      return `MissionInterviewDestroyed`;

    case TIMELINE_EVENTS.commentCreated:
      return `CommentCreated`;

    case TIMELINE_EVENTS.commentUpdated:
      return `CommentUpdated`;

    case TIMELINE_EVENTS.commentDestroyed:
      return `CommentDestroyed`;

    case TIMELINE_EVENTS.deelTaskCreated:
      return `DeelTaskCreated`;

    case TIMELINE_EVENTS.deelTaskUpdated:
      return `DeelTaskUpdated`;

    case TIMELINE_EVENTS.profileEducationCreated:
      return `ProfileEducationCreated`;

    case TIMELINE_EVENTS.profileEducationUpdated:
      return `ProfileEducationUpdated`;

    case TIMELINE_EVENTS.profileEducationDestroyed:
      return `ProfileEducationDestroyed`;

    case TIMELINE_EVENTS.profileLinkCreated:
      return `ProfileLinkCreated`;

    case TIMELINE_EVENTS.profileLinkUpdated:
      return `ProfileLinkUpdated`;

    case TIMELINE_EVENTS.profileLinkDestroyed:
      return `ProfileLinkDestroyed`;

    case TIMELINE_EVENTS.profileScheduleCreated:
      return `ProfileScheduleCreated`;

    case TIMELINE_EVENTS.profileScheduleUpdated:
      return `ProfileScheduleUpdated`;

    case TIMELINE_EVENTS.profileScheduleDestroyed:
      return `ProfileScheduleDestroyed`;

    case TIMELINE_EVENTS.profileVacationCreated:
      return `ProfileVacationCreated`;

    case TIMELINE_EVENTS.profileVacationUpdated:
      return `ProfileVacationUpdated`;

    case TIMELINE_EVENTS.profileVacationDestroyed:
      return `ProfileVacationDestroyed`;

    case TIMELINE_EVENTS.profileWorkExperienceCreated:
      return `ProfileWorkExperienceCreated`;

    case TIMELINE_EVENTS.profileWorkExperienceUpdated:
      return `ProfileWorkExperienceUpdated`;

    case TIMELINE_EVENTS.profileWorkExperienceDestroyed:
      return `ProfileWorkExperienceDestroyed`;

    case TIMELINE_EVENTS.profileListCreated:
      return `ProfileListCreated`;

    case TIMELINE_EVENTS.profileListUpdated:
      return `ProfileListUpdated`;

    case TIMELINE_EVENTS.profileListDestroyed:
      return `ProfileListDestroyed`;

    case TIMELINE_EVENTS.timeCardCreated:
      return `TimeCardCreated`;

    case TIMELINE_EVENTS.timeCardUpdated:
      return `TimeCardUpdated`;

    case TIMELINE_EVENTS.timeCardDestroyed:
      return `TimeCardDestroyed`;

    case TIMELINE_EVENTS.timeSlipCreated:
      return `TimeSlipCreated`;

    case TIMELINE_EVENTS.timeSlipUpdated:
      return `TimeSlipUpdated`;

    case TIMELINE_EVENTS.timeSlipDestroyed:
      return `TimeSlipDestroyed`;

    case TIMELINE_EVENTS.coderbyteChallengeResponseCreated:
      return `CoderbyteChallengeResponseCreated`;

    case TIMELINE_EVENTS.coderbyteChallengeResponseUpdated:
      return `CoderbyteChallengeResponseUpdated`;

    case TIMELINE_EVENTS.coderbyteChallengeResponseDestroyed:
      return `CoderbyteChallengeResponseDestroyed`;

    case TIMELINE_EVENTS.interviewResponseCreated:
      return `InterviewResponseCreated`;

    case TIMELINE_EVENTS.interviewResponseUpdated:
      return `InterviewResponseUpdated`;

    case TIMELINE_EVENTS.interviewResponseDestroyed:
      return `InterviewResponseDestroyed`;

    case TIMELINE_EVENTS.assignmentCreated:
      return `AssignmentCreated`;

    case TIMELINE_EVENTS.assignmentUpdated:
      return `AssignmentUpdated`;

    case TIMELINE_EVENTS.assignmentDestroyed:
      return `AssignmentDestroyed`;

    default:
      return getTimelineTitleKeyByEventType(kName, payload, userProp);
  }
};

/**
 * Gets data structure based on resource type
 *
 * @param {String} resourceType
 * @param {Object} resourceContext
 *
 * @returns Object
 */
function getDataByResourceType(resourceType, resourceContext, payload, userProp) {
  switch (resourceType) {
    case RESOURCE_TYPES.assignment:
      return {
        user: userProp?.profile ? userProp?.profile : userProp,
        teamName: resourceContext?.team?.name,
      };
    case RESOURCE_TYPES.profile:
      return {
        user: resourceContext,
      };
    case RESOURCE_TYPES.missionRoleApplicant:
      return {
        user: resourceContext?.profile,
        role: resourceContext?.missionRole,
        fromStage: payload?.state?.slice(0, 1),
        toStage: payload?.state?.slice(-1),
      };
    case RESOURCE_TYPES.missionRoleInvitation:
      return {
        user: resourceContext?.profile,
        role: resourceContext?.missionRole,
      };
    case RESOURCE_TYPES.missionInterviewCreated:
      return {
        user: userProp?.profile ? userProp?.profile : userProp,
        interviewer: resourceContext?.interviewer,
        role: resourceContext?.missionRole,
        url: payload?.calendlyLink,
        date: payload?.createdAt,
      };
    case RESOURCE_TYPES.comment:
      return {
        user: resourceContext?.user,
        commentableType: resourceContext.commentable?.type,
      };
    case RESOURCE_TYPES.deelInvoicesTask:
      return {
        user: resourceContext?.assignment?.profile,
        acceptedBy: resourceContext?.acceptedBy,
        description: resourceContext?.description,
        status: resourceContext?.status,
      };
    case RESOURCE_TYPES.profileEducation:
      return {
        user: userProp?.profile ? userProp?.profile : userProp,
        schoolName: resourceContext?.schoolName,
      };
    case RESOURCE_TYPES.profileLink:
      return {
        user: userProp?.profile ? userProp?.profile : userProp,
        linkType: resourceContext?.linkType,
      };
    case RESOURCE_TYPES.profileSchedule:
      return {
        user: userProp?.profile ? userProp?.profile : userProp,
        weekday: resourceContext?.weekday,
      };
    case RESOURCE_TYPES.profileVacation:
      return {
        user: userProp?.profile ? userProp?.profile : userProp,
        vacationStartDate: resourceContext?.startDate,
        vacationEndDate: resourceContext?.endDate,
      };
    case RESOURCE_TYPES.profileWorkExperience:
      return {
        user: userProp?.profile ? userProp?.profile : userProp,
        companyName: resourceContext?.companyName,
        experienceRole: resourceContext?.role,
      };
    case RESOURCE_TYPES.profileList:
      return {
        user: userProp?.profile ? userProp?.profile : userProp,
        listName: resourceContext?.list?.title,
        profile: resourceContext?.profile,
      };
    case RESOURCE_TYPES.coderbyteChallengeResponse:
      return {
        user: userProp?.profile ? userProp?.profile : userProp,
        challengeName: resourceContext?.challenge?.name,
      };
    case RESOURCE_TYPES.interviewResponse:
      return {
        user: userProp?.profile ? userProp?.profile : userProp,
        interviewName: resourceContext?.interview?.name,
      };
    case RESOURCE_TYPES.timeCard: {
      let date = new Date(resourceContext?.monthPeriod);
      let formattedDate = `${date.toLocaleString("default", { month: "long" })}, ${date.getFullYear()}`;

      return {
        user: userProp?.profile ? userProp?.profile : userProp,
        timeCardMonthPeriod: formattedDate,
        timeCardStatus: resourceContext?.status,
      };
    }
    case RESOURCE_TYPES.timeSlip: {
      let monthPeriod = new Date(resourceContext?.timeCard?.monthPeriod);
      let formattedMonthPeriod = `${monthPeriod.toLocaleString("default", {
        month: "long",
      })}, ${monthPeriod.getFullYear()}`;

      return {
        user: userProp?.profile ? userProp?.profile : userProp,
        activityDate: resourceContext?.activityDate,
        activityType: resourceContext?.activityType,
        timeLogged: resourceContext?.timeLogged,
        teamName: resourceContext?.team?.name,
        timeCardMonthPeriod: formattedMonthPeriod,
        timeCardStatus: resourceContext?.timeCard?.status,
      };
    }
    case RESOURCE_TYPES.missionRole:
      return {
        user: userProp,
        mission: resourceContext?.mission,
        role: resourceContext,
      };
    case RESOURCE_TYPES.missionRoleChallenge:
      return {
        user: userProp,
        mission: resourceContext?.missionRole?.mission,
        role: resourceContext?.missionRole,
        codeChallenge: resourceContext?.challenge,
      };
    case RESOURCE_TYPES.locatable:
      return getPolymorphicResourceData("location", "locatable", userProp, resourceContext);
    case RESOURCE_TYPES.taggable:
      return getPolymorphicResourceData("tag", "taggable", userProp, resourceContext);
    case RESOURCE_TYPES.missionRoleQuestion:
      return {
        user: userProp,
        question: resourceContext,
        role: resourceContext?.assessment?.assessmentable,
        mission: resourceContext?.assessment?.assessmentable?.mission,
      };
    default:
      return {};
  }
}

const styles = {
  question_wording: css`
    color: #7878a1;
    font-weight: 600;
  `,
};

export { formatTimelineResourceData, getTimelineTitleKey, TIMELINE_EVENT_TYPES };

export default TIMELINE_EVENTS;

// TO BE IMPLEMENTED
/*          
platform:backend:profile:destroyed
platform:backend:interview_review:created
platform:backend:interview_review:updated
platform:backend:interview_review:destroyed
platform:backend:mission_role_invitation:updated
platform:backend:mission_role_invitation:destroyed
platform:backend:profile_interview:created
platform:backend:profile_interview:updated
platform:backend:profile_interview:destroyed
platform:backend:state_transition:created
platform:backend:state_transition:updated
platform:backend:state_transition:destroyed
platform:backend:tagging:created
platform:backend:tagging:updated
platform:backend:tagging:destroyed
platform:backend:team_assignment_period:created
platform:backend:team_assignment_period:updated
platform:backend:team_assignment_period:destroyed
platform:backend:team_profile_account:created
platform:backend:team_profile_account:updated
platform:backend:team_profile_account:destroyed
*/
